import { proxy } from "valtio"
import { EmblemApi } from "../api/wizard"
import { Steps, Views } from "../constants"
import { Circle, CircleData, ColorTarget, Step, StepOption } from "../types/wizard"
import { createDesign, designPrice, getVolumePrices} from "../api/art";
import { DesignDoc, getPriceAndDiscount } from "../types/emblemData";

// Default store state
const defaultState = {
  target: {} as ColorTarget,
  view: Views.EMBLEMS,
  designRequest: {} as any,
  step: {} as Step,
  loading: false,
  stepIndex: -1,
  steps: [] as Step[],
  emblemData: {} as Record<string, any>,
  requireIndication: false as boolean,
  isEmblemLeather: false as boolean,
  editedCircleColor: null as CircleData | null,
  hoveredColorId: 0 as number,
  pricing: {} as any,
  activePrice: {} as any
};

export const wizardStore = proxy({ ...defaultState });

export async function fetchPriceFromHost() {
  try {
    wizardStore.loading = true;
    const customerId = localStorage.getItem("customerId") ?? '';
    const wizardData = DesignDoc.transformToDesignDoc(wizardStore.emblemData, customerId) ;

    const data =  {
      action: 'volumePricesQuery',
      productId: wizardData.itemNo,
      items: {}
    }

    window.parent.postMessage(data, '*');
    //need to implement a listener for a parent action
  } catch (error) {
    console.error("Error fetching price:", error);
  } finally {
    wizardStore.loading = false;
  }
}

export async function fetchPriceFromSANA() {
  try {
    wizardStore.loading = true;
    const customerId = localStorage.getItem("customerId") ?? '';
    const wizardData = DesignDoc.transformToDesignDoc(wizardStore.emblemData, customerId) ;
    const ret = await getVolumePrices(wizardData.itemNo as string);

    if (ret.success)
    {
     const pricingData = ret.response.catalog.volumePrices ;
     const activePrice = getPriceAndDiscount(pricingData, wizardStore.emblemData.SizeStandard, "HA", 1) 
     wizardStore.pricing = pricingData;
     wizardStore.activePrice = activePrice ;
    }

  } catch (error) {
    console.error("Error fetching price:", error);
  } finally {
    wizardStore.loading = false;
  }
}

export async function fetchPrice() {
  try {
    wizardStore.loading = true;
    const customerId = localStorage.getItem("customerId") ?? '';
    const wizardData = DesignDoc.transformToDesignDoc(wizardStore.emblemData, customerId) ;
    const ret = await createDesign(wizardData);
    if (ret.success && ret.designId)
    {
      const pricingData = await designPrice(ret.designId);
      wizardStore.pricing = pricingData;
    }

  } catch (error) {
    console.error("Error fetching price:", error);
  } finally {
    wizardStore.loading = false;
  }
}

export const addTarget = (stepId: string, circle: Circle) => {
  wizardStore.target = {
    ...wizardStore.target,
    [stepId]: { circle }
  };
};

export const resetWizardStore = () => {
  Object.assign(wizardStore, defaultState);
};

export const startFromProductType = (stepId: string, newData: any) => {
  const existingData = wizardStore.emblemData[stepId] || {};
  wizardStore.emblemData = {
    [stepId]: {
      ...existingData,
      ...newData,
    },
  };
};

export const goToStep = (stepId: string) => {
  const stepPosition = wizardStore.steps.findIndex((s) => s.id === stepId);
  wizardStore.step = wizardStore.steps[stepPosition];
}

export const saveEmblemData = (stepId: string, newData: any) => {
  const existingData = wizardStore.emblemData[stepId] || {}
  wizardStore.emblemData = {
    ...wizardStore.emblemData,
    [stepId]: {
      ...existingData,
      ...newData
    }
  }
}

export const removeStepEmblemData = (stepId: string) => {
  const existingData = wizardStore.emblemData[stepId];
  if (existingData) delete wizardStore.emblemData[stepId];
}

export const editColorFromDetails = (circleData: CircleData | null) => {
  wizardStore.editedCircleColor = circleData;
}

export const setHoveredColor = (circleId: number) => {
  wizardStore.hoveredColorId = circleId;
}

export const checkIfColorIsBeingUsed = (color: CircleData) => {
  const { requireIndication, emblemData } = wizardStore;

  if (requireIndication && emblemData[Steps.Emblem3d]) {
    const indications: CircleData[] = emblemData[Steps.Emblem3d].items;
    const existColor = indications?.find(i => i.color === color.color);

    if (existColor) {
      return true;
    }
  }
  return false;
};

export async function getSteps({ updateSteps }: { updateSteps: boolean }) {
  const steps = await EmblemApi.getSteps({ updateSteps }) as Step[]
  wizardStore.steps = steps
  wizardStore.stepIndex = 0
  wizardStore.step = wizardStore.steps[wizardStore.stepIndex]
  wizardStore.loading = false
}

export async function getNewDesignRequest() {
  const designRequest = await EmblemApi.getNewDesignRequest()
  wizardStore.designRequest = designRequest
}

export async function nextStep(lastOption?: StepOption, currentStepId?: string) {
  const { steps } = wizardStore;

  if (!steps || steps.length === 0) {
    console.error("No steps available in the wizard.");
    return;
  }

  // get stepIndex by step id
  const stepPosition = steps.findIndex((s) => s.id === currentStepId);
  if (stepPosition !== -1) {
    // Update index based on position.
    wizardStore.stepIndex = stepPosition + 1;
  }

  // update the steps only if needed
  if (lastOption && lastOption.updateSteps) {
    wizardStore.loading = true
    const steps = await EmblemApi.getSteps(wizardStore.designRequest)
    wizardStore.steps = steps as Step[]
    wizardStore.requireIndication = steps.some((s: Step) => s.id === Steps.Emblem3d || s.id === Steps.EmblemPrintStitch);
    wizardStore.isEmblemLeather = steps.some((s: Step) => s.id === Steps.EmblemLeather);
  }

  // check if stepIndex is out of bounds
  if (wizardStore.stepIndex < 0 || wizardStore.stepIndex > wizardStore.steps.length) {
    return;
  }

  wizardStore.step = wizardStore.steps[wizardStore.stepIndex]
  wizardStore.loading = false
}

export function previousStep() {
  wizardStore.stepIndex--
  wizardStore.step = wizardStore.steps[wizardStore.stepIndex]
}

export function setProductClass(productClass: StepOption, currentStepId: string) {
  wizardStore.designRequest.productClass = { id: productClass.id }
  nextStep(productClass, currentStepId);
}

export function setProductSubType(productSubType: StepOption, currentStepId: string) {
  wizardStore.designRequest.productType = { id: productSubType.id }
  nextStep(productSubType, currentStepId)
}

export function setSizeStandard(sizeStandard: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.dimensions = { id: sizeStandard.id }
  nextStep(sizeStandard, currentStepId)
}

export function setFabricColor(fabricColor: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.fabricColor = { id: fabricColor.id }
  nextStep(fabricColor, currentStepId)
}

export function setBacking(backing: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.backing = { id: backing.id }
  nextStep(backing, currentStepId)
}

export function setDesignType(designType: StepOption, currentStepId: string) {
  wizardStore.designRequest.createDesign.createDesignOption = designType.code
  nextStep(designType, currentStepId)
}

export function setThreadColors(threadColors: StepOption, currentStepId: string) {
  wizardStore.designRequest.threadColors.colors.push(threadColors)
  nextStep(threadColors, currentStepId)
}

export function setAllowWEItoChooseColors(currentStepId: string, selected: boolean) {
  if (!wizardStore.designRequest[currentStepId]) {
    wizardStore.designRequest[currentStepId] = {
      allowWEItoChoose: false,
    };
  }

  wizardStore.designRequest[currentStepId].allowWEItoChoose = selected;

  if (selected && wizardStore.emblemData[currentStepId]?.items)
    wizardStore.emblemData[currentStepId].items = [];
}

export function set3DEmblemOption(currentStepId: string, selectedOption: string) {
  if (!wizardStore.designRequest.productOptions.emblem3D) {
    wizardStore.designRequest.productOptions.emblem3D = {
      set3DEmblemOption: "IndicateParts",
    };
  }

  wizardStore.designRequest.productOptions.emblem3D.set3DEmblemOption = selectedOption;

  if (selectedOption === "AllowWEIToChoose" && wizardStore.emblemData[currentStepId]?.items)
    wizardStore.emblemData[currentStepId].items = [];
}

export function setEmblemPrintStitchOption(currentStepId: string, selectedOption: string) {
  if (!wizardStore.designRequest.productOptions.emblemPrintStitch) {
    wizardStore.designRequest.productOptions.emblemPrintStitch = {
      setEmblemPrintStitchOption: "IndicateParts",
    };
  }

  wizardStore.designRequest.productOptions.emblemPrintStitch.setEmblemPrintStitchOption = selectedOption;

  if (selectedOption === "AllowWEIToChoose" && wizardStore.emblemData[currentStepId]?.items)
    wizardStore.emblemData[currentStepId].items = [];
}

export function setRequestDetails(requestDetails: StepOption, currentStepId: string) {
  nextStep(requestDetails, currentStepId)
}

export function setEmblem3d(emblem3d: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.emblem3d = { id: emblem3d?.id }
  nextStep(emblem3d, currentStepId)
}

export function setBorderType(borderType: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.borderType = { id: borderType.id }
  nextStep(borderType, currentStepId)
}

export function setBorderColor(borderColor: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.borderColor = { id: borderColor.id }
  nextStep(borderColor, currentStepId)
}

export function setShape(shape: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.shapeSizeOther = { id: shape.id }
  nextStep(shape, currentStepId)
}

export function setLayout(layout: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.layout = { id: layout.id }
  nextStep(layout, currentStepId)
}

export function setBackgroundPattern(backgroundPattern: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.backgroundPattern = { id: backgroundPattern.id }
  nextStep(backgroundPattern, currentStepId)
}

export function setSewingChannel(sewingChannel: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.sewingChannel = { id: sewingChannel.id }
  nextStep(sewingChannel, currentStepId)
}

export function setEmblemLeather(emblemLeather: StepOption, currentStepId: string) {
  //wizardModel.designRequest.productOptions.emblemLeather = { id: emblemLeather.id }
  nextStep(emblemLeather, currentStepId)
}

export function setFabricType(fabricType: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.fabricType = { id: fabricType.id }
  nextStep(fabricType, currentStepId)
}

export function setGarmentType(garmentType: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.garmentType = { id: garmentType.id }
  nextStep(garmentType, currentStepId)
}

export function setLocation(location: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.location = { id: location.id }
  nextStep(location, currentStepId)
}

export function setHolographicColors(holographicColors: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.holographicColors = { id: holographicColors.id }
  nextStep(holographicColors, currentStepId)
}

export function setHolographicUpload(holographicUpload: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.holographicColors = { id: holographicUpload.id }
  nextStep(holographicUpload, currentStepId)
}

export function setScrolling(scrolling: any) {
  const { scrollingText, scrollingFont, scrollingFontDisplayName } = scrolling;

  wizardStore.designRequest.productOptions = {
    ...wizardStore.designRequest.productOptions,
    scrollingText,
    scrollingFont,
    scrollingFontDisplayName
  };
}

export function setCombination(combination: StepOption, currentStepId: string) {
  wizardStore.designRequest.productOptions.combination = { id: combination.id }
  nextStep(combination, currentStepId)
}

export const setStepCallback = {
  [Steps.ProductType]: setProductClass,
  [Steps.ProductSubtype]: setProductSubType,
  [Steps.Shape]: setShape,
  [Steps.SizeStandard]: setSizeStandard,
  [Steps.FabricColor]: setFabricColor,
  [Steps.FabricType]: setFabricType,
  [Steps.Backing]: setBacking,
  [Steps.DesignType]: setDesignType,
  [Steps.BorderType]: setBorderType,
  [Steps.BorderColor]: setBorderColor,
  [Steps.GarmentType]: setGarmentType,
  [Steps.Location]: setLocation,
  [Steps.Layout]: setLayout,
  [Steps.HolographicColors]: setHolographicColors,
  [Steps.HolographicUpload]: setHolographicUpload,
  [Steps.ThreadColors]: setThreadColors,
  [Steps.SewingChannel]: setSewingChannel,
  [Steps.Emblem3d]: setEmblem3d,
  [Steps.Scrolling]: setScrolling,
  [Steps.Combination]: setCombination
}